import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { queryClient } from "@circle-react/providers/QueryClient/reactQueryConfig";
import { flatten, noop, uniqBy } from "lodash";
import { useCallback, useMemo } from "react";
import type { QueryKey } from "react-query";
import { useInfiniteQuery } from "react-query";

import type { ChatRoomParticipant } from "@/react/types/Chat/ChatRoomParticipant";

export const PARTICIPANTS_DEFAULT_PER_PAGE = 25;

interface UseChatParticipantsApiParams {
  enabled?: boolean;
  per_page?: number;
  shouldDisableCache?: boolean;
}

export const useChatParticipantsApi = (
  chatRoomUuid?: string,
  params: UseChatParticipantsApiParams = {},
) => {
  const queryKey: QueryKey = ["chat-participants", chatRoomUuid];
  const {
    shouldDisableCache = false,
    enabled: isEnabled = true,
    per_page = PARTICIPANTS_DEFAULT_PER_PAGE,
    ...apiParams
  } = params;

  const fetchParticipants = (page: number) =>
    reactQueryGet(
      internalApi.chatRoomParticipants.index({
        uuid: chatRoomUuid,
        params: { page, per_page, ...apiParams },
      }),
    );

  const getCachedParticipants = useCallback(() => {
    const data = queryClient.getQueryData<{
      pages?: Array<{ records: Array<ChatRoomParticipant> }>;
    }>(queryKey);
    const participants = flatten((data?.pages || []).map(page => page.records));
    return uniqBy(participants, "id");
  }, [queryKey]);

  const cachedParticipantsCount = getCachedParticipants().length;
  const isQueryEnabled =
    Boolean(chatRoomUuid) &&
    isEnabled &&
    (cachedParticipantsCount === 0 || shouldDisableCache);

  const {
    data,
    isInitialLoading: isLoading,
    refetch: refetchChatParticipants,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    initialPageParam: 1,
    queryKey,
    queryFn: ({ pageParam = 1 }) => fetchParticipants(pageParam),
    select: data => ({
      pages: data.pages,
      pageParams: data.pageParams,
      participants: uniqBy(
        flatten((data.pages || []).map(page => page.records)),
        "id",
      ),
      totalCount: data.pages[0].count,
    }),
    enabled: isQueryEnabled,
    getNextPageParam: lastPage =>
      lastPage.has_next_page ? lastPage.page + 1 : undefined,
  });

  const { totalCount = 0 }: any = data || {};

  const memoizedParticipants = useMemo(
    () => getCachedParticipants(),
    [data?.pages],
  );

  return {
    chatParticipants: memoizedParticipants,
    isLoading,
    refetchChatParticipants: chatRoomUuid ? refetchChatParticipants : noop,
    hasNextPage: hasNextPage,
    totalCount,
    fetchNextPage,
  };
};
